import React from 'react';
import { FileUpload } from '@emorg-prd/standard-react';
import PortOpeningRule from '../../../../models/entities/PortOpeningRule';
import { PortOpeningRequestsDirection } from '../../../../models/enums/POR/PortOpeningRequestsDirection';
import PortOpeningRequestService from '../../../../services/PortOpeningRequestService';
import CSVGeneratorButton from '../../../common/CSVGeneratorButton';
import { RuleColumnsLayout, PORColumn } from '../RuleColumnsLayout';
import StandardModal from '../../../common/modals/StandardModal';
import { Button, ButtonGroup, Icon, Alert } from '../../../common';
import './RuleModal.css'

interface BulkUploadModalProps {
	direction: PortOpeningRequestsDirection;
	subscriptionRequestId: number;
	onSuccess: (rules: PortOpeningRule[]) => any;
	onCancel: (...args: any[]) => any;
	visible: boolean;
}

interface BulkUploadModalState {
	loading: boolean;
	file: any;
	fileKey: number;
	alert: {
		visible: boolean;
		text?: string;
	},
}

export default class BulkUploadModal
	extends React.Component<BulkUploadModalProps, BulkUploadModalState> {
	
	portOpeningRequestService: PortOpeningRequestService;

	constructor(props: BulkUploadModalProps) {
		super(props);
		this.portOpeningRequestService = new PortOpeningRequestService();
		this.state = {
			loading: false,
			file: null,
			fileKey: 0,
			alert: {
				visible: false,
			},
		};
	}

	handleChange = (file) => {
		this.setState({
			file: file[0],
			alert: {
				visible: false,
			}
		});
	};

	handleUpload = async () => {
		try{
			this.setState({
				loading: true
			});
			await this.portOpeningRequestService.parse(this.props.direction, this.state.file, Number(this.props.subscriptionRequestId))
			.then((response: PortOpeningRule[]) => {
				const rules = response.map(r => new PortOpeningRule(r));
				this.props.onSuccess(rules);
			})
		}catch(err){
			this.handleFileRemoval();
			this.setState({
				alert: {
					visible: true,
					text: err.message
				}
			});
		}finally{
			this.setState({
				loading: false,
				file: null,
			});
		}
	};

	handleCancel = () => {
		this.setState({
			loading: false,
			file: null,
		});
		this.props.onCancel();
	}

	handleFileRemoval = () => {
		this.setState((prev) => ({
			file: null,
			fileKey: prev.fileKey + 1,
			alert: { visible: false },
		}));
	}

	// The backend parser has no 'Being Attested' column for SubscriptionToInternet, so it is left out of the template.
	getTemplateColumns = (): PORColumn[] => {
		const columns = RuleColumnsLayout.get(this.props.direction) ?? [];
		if (this.props.direction?.name === PortOpeningRequestsDirection.SubscriptionToInternet.name) {
			return columns.filter((column) => column.header !== 'Being Attested');
		}
		return columns;
	};

	render() {
		return (
			<StandardModal
				show={this.props.visible}
				title="Import rules to the request"
				onDismiss={this.handleCancel}
				footer={
					<ButtonGroup>
						<Button
							variant="primary"
							onClick={this.handleUpload}
							isLoading={this.state.loading}
							disabled={(!this.state.file) || this.state.alert.visible}
						>
							Upload Rules
						</Button>
						<Button
							variant="secondary"
							onClick={this.handleFileRemoval}
							isLoading={this.state.loading}
							disabled={(!this.state.file) || this.state.alert.visible}
						>
							<Icon icon='trashcan' size='small' className={"em-u-margin-right-half"}></Icon>
							Remove File
						</Button>
					</ButtonGroup>
				}
			>
				<CSVGeneratorButton
					label="Download Template"
					columns={this.getTemplateColumns()}
					fileName={`Template-Rule-${this.props.direction?.name}.csv`}
					data={[]}
				/>
				<FileUpload
					key={this.state.fileKey}
					multiple={false}
					onChange={this.handleChange}
				/>
				{this.state.alert.visible &&
					<Alert
						variant="error"
						body={this.state.alert.text}
						onClose={() => {
							this.setState({ alert: { visible: false } });
						}}
					>
						{this.state.alert.text}
					</Alert>}
			</StandardModal>
		);
	}
}